/** @format */

import { useState, useCallback, useRef } from "react";
import { Brand, SearchParams, SearchResponse } from "@question-bank/shared";
import { searchQuestions } from "../api/questions";

interface UseSearchReturn {
	results: SearchResponse | null;
	isLoading: boolean;
	error: string | null;
	search: (params: SearchParams) => Promise<void>;
	reset: () => void;
}

export const useSearch = (brand: Brand): UseSearchReturn => {
	const [results, setResults] = useState<SearchResponse | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const requestIdRef = useRef(0);

	const search = useCallback(
		async (params: SearchParams) => {
			const requestId = ++requestIdRef.current;
			setIsLoading(true);
			setError(null);

			try {
				const response = await searchQuestions(brand, params);
				if (requestIdRef.current === requestId) {
					setResults(response);
				}
			} catch (err) {
				if (requestIdRef.current === requestId) {
					setError(err instanceof Error ? err.message : "Search failed");
					setResults(null);
				}
			} finally {
				if (requestIdRef.current === requestId) {
					setIsLoading(false);
				}
			}
		},
		[brand],
	);

	const reset = useCallback(() => {
		setResults(null);
		setError(null);
	}, []);

	return { results, isLoading, error, search, reset };
};
